<div class="security_container">
  <ui-page-header
    [title]="'settings_security.title' | translate"
    [lucideIcon]="'ShieldCheck'"
  ></ui-page-header>

  <div class="content_plan">
    <!-- *ngIf on the host, not inside it: an empty flex child still takes the
         .content_plan gap. -->
    <ui-auth-error-banner *ngIf="error" [error]="error"></ui-auth-error-banner>

    <ng-container *ngIf="step === 'status' || step === 'done'">
      <div class="form-content">
        <div class="form-content-title">{{ 'settings_security.two_step' | translate }}</div>

        <p class="security__lede">{{ 'settings_security.lede' | translate }}</p>

        <p class="security__notice" *ngIf="step === 'done'" data-testid="mfa-enrolled">
          {{ 'settings_security.enrolled' | translate }}
        </p>

        <p class="security__notice" *ngIf="status?.required" data-testid="mfa-required-notice">
          {{ 'settings_security.required_notice' | translate }}
        </p>

        <ul class="security__factors" *ngIf="factors.length; else noFactorsOrError">
          <li class="security__factor" *ngFor="let factor of factors">
            <span class="security__factor-name">
              {{ 'settings_security.method.' + factor.method | translate }}
            </span>
            <span class="security__factor-state" [attr.data-state]="factor.state">
              {{ 'settings_security.state.' + factor.state | translate }}
            </span>
            <span class="security__factor-used" *ngIf="factor.last_used_at">
              {{ 'settings_security.last_used' | translate }}
              {{ factor.last_used_at | adaptToUserUtcOffset | date: 'short' }}
            </span>
            <button
              type="button"
              class="security__link security__link--danger"
              [attr.data-testid]="'mfa-disable-' + factor.method"
              (click)="startDisable(factor.method)"
            >
              {{ 'settings_security.turn_off' | translate }}
            </button>
          </li>
        </ul>

        <!-- "Could not load" is not "nothing enrolled". -->
        <ng-template #noFactorsOrError>
          <p
            class="security__notice security__notice--warn"
            *ngIf="statusFailed; else noFactors"
            data-testid="mfa-status-unavailable"
          >
            {{ 'settings_security.status_unavailable' | translate }}
          </p>

          <ng-template #noFactors>
            <p class="security__lede" data-testid="mfa-no-factors">
              {{ 'settings_security.no_factors' | translate }}
            </p>
          </ng-template>
        </ng-template>

        <div class="security__actions">
          <!-- Enrolling against an unknown state invites a 409. -->
          <ui-button
            *ngIf="statusFailed"
            type="secondary"
            testId="mfa-status-retry"
            [label]="'common.retry' | translate"
            [disabled]="loading"
            (click)="loadStatus()"
          ></ui-button>

          <ui-button
            *ngIf="!statusFailed && !isActive('totp')"
            type="primary"
            testId="mfa-setup-totp"
            [label]="'settings_security.set_up_authenticator' | translate"
            [disabled]="loading"
            (click)="startEnrolment('totp')"
          ></ui-button>

          <!-- The server refuses the enrolment where Email OTP is off. -->
          <ui-button
            *ngIf="!statusFailed && emailAvailable && !isActive('email')"
            type="secondary"
            testId="mfa-setup-email"
            [label]="'settings_security.set_up_email' | translate"
            [disabled]="loading"
            (click)="startEnrolment('email')"
          ></ui-button>
        </div>
      </div>

      <!-- Turning a factor off is step-up gated while another one is active. -->
      <app-recovery-codes-panel
        *ngIf="stepUpRequired && !disabling"
        [state]="recovery"
        [disabled]="loading"
        [canSendEmailCode]="canSendStepUpCode"
        (regenerated)="loadStatus()"
      ></app-recovery-codes-panel>

      <div class="form-content" *ngIf="disabling">
        <div class="form-content-title">{{ 'settings_security.turn_off' | translate }}</div>
        <p class="security__lede">{{ 'settings_security.turn_off_confirm' | translate }}</p>

        <form
          class="security__form"
          [formGroup]="disableForm"
          (ngSubmit)="confirmDisable()"
          (keydown.enter)="onEnterDisable($event)"
        >
          <ui-form-field
            *ngIf="stepUpRequired"
            [id]="'mfa-disable-code'"
            testId="mfa-disable-code"
            type="text"
            formControlName="code"
            autocomplete="one-time-code"
            inputMode="numeric"
            [maxLength]="6"
            [label]="'settings_security.step_up_code' | translate"
          ></ui-form-field>

          <div class="security__actions">
            <ui-button
              type="danger"
              testId="mfa-disable-confirm"
              [label]="'settings_security.turn_off' | translate"
              [disabled]="loading"
            ></ui-button>

            <!-- An email-OTP-only user has no authenticator to read a code
                 from, and re-enrolling email 409s while it is active. -->
            <button
              *ngIf="stepUpRequired && canSendStepUpCode"
              type="button"
              class="security__link"
              data-testid="mfa-disable-send-code"
              [disabled]="sendingStepUpCode || stepUpResendWait > 0"
              (click)="sendStepUpCode()"
            >
              <ng-container *ngIf="stepUpResendWait > 0; else sendStepUpNow">
                {{ 'settings_security.resend_in' | translate: { seconds: stepUpResendWait } }}
              </ng-container>
              <ng-template #sendStepUpNow>
                {{ 'settings_security.send_code_by_email' | translate }}
              </ng-template>
            </button>

            <button type="button" class="security__link" (click)="cancelDisable()">
              {{ 'common.cancel' | translate }}
            </button>
          </div>
        </form>
      </div>

      <!-- Tenant-wide, unlike everything above. `access:`, not `edit:`,
           because that is what the panel's own GET checks; the panel asks for
           `edit:` itself before offering to save. -->
      <app-mfa-policy-panel
        *appHasPermission="{ permission: 'access:settings_security' }"
      ></app-mfa-policy-panel>
    </ng-container>

    <!-- Mounted per attempt: the seed lives in the wizard, so leaving the
         step destroys it. The prompt shown after login mounts the same
         component. -->
    <div class="form-content" *ngIf="step === 'enrolling'">
      <app-mfa-enrolment-wizard
        [method]="method"
        (enrolled)="onEnrolled($event)"
        (cancelled)="cancelEnrolment()"
      ></app-mfa-enrolment-wizard>
    </div>
  </div>
</div>
